import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatPrice, formatImageUrl, categoryNames } from '../utils/helpers';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { ShoppingCart, ArrowLeft, Check } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { addToast } = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    const result = await addToCart(product.id, quantity);
    if (result.success) {
      setAddedToCart(true);
      addToast(`${quantity}x ${product.name} hozzáadva a kosárhoz!`, 'success');
      setTimeout(() => setAddedToCart(false), 2000);
    } else {
      addToast(result.error, 'error');
    }
  };

  if (loading) {
    return (
      <div className="relative min-h-screen pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-300">Betöltés...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="relative min-h-screen pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-300">Termék nem található</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-yellow-200 transition mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Vissza
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="glass rounded-2xl overflow-hidden aspect-square">
            {product.images && product.images.length > 0 ? (
              <img
                src={formatImageUrl(product.images[0])}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-black/30">
                <span className="text-9xl">✨</span>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            <div className="text-sm text-yellow-200/70 mb-2">
              {categoryNames[product.category]}
            </div>
            <h1 className="text-4xl font-bold mb-4">{product.name}</h1>
            <div className="text-3xl font-bold text-yellow-200 mb-6">
              {formatPrice(product.price)}
            </div>

            <div className="glass rounded-xl p-6 mb-6">
              <p className="text-gray-300 leading-relaxed">{product.description}</p>
            </div>

            {/* Stock Info */}
            {product.stock > 0 ? (
              <div className="flex items-center gap-2 text-green-400 mb-6">
                <Check className="w-5 h-5" />
                <span>Raktáron: {product.stock} db</span>
              </div>
            ) : (
              <div className="text-red-400 mb-6">Jelenleg nincs raktáron</div>
            )}

            {/* Quantity Selector */}
            {product.stock > 0 && (
              <div className="mb-6">
                <label className="block text-sm text-gray-400 mb-2">Mennyiség</label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition"
                  >
                    -
                  </button>
                  <span className="text-2xl font-semibold w-16 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition"
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            {/* Add to Cart Button */}
            {product.stock > 0 && (
              <button
                onClick={handleAddToCart}
                className={`w-full btn-magic rounded-lg py-4 text-lg font-semibold flex items-center justify-center gap-3 transition ${
                  addedToCart ? 'bg-green-600' : ''
                }`}
              >
                {addedToCart ? (
                  <>
                    <Check className="w-6 h-6" />
                    Kosárba helyezve!
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-6 h-6" />
                    Kosárba helyezés
                  </>
                )}
              </button>
            )}

            {/* Digital Product Info */}
            {product.is_digital && (
              <div className="glass rounded-xl p-4 mt-6">
                <p className="text-sm text-gray-400">
                  🔒 Digitális termék - Letöltési link a vásárlás után
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .btn-magic {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          border: 1px solid hsl(0 0% 100% / 0.14);
        }
        .btn-magic:before {
          content: '';
          position: absolute;
          inset: -200%;
          background: conic-gradient(from 0deg, transparent, hsl(45 86% 62% / 0.25), transparent 30%);
          animation: spin 6s linear infinite;
          z-index: -1;
          filter: blur(12px);
        }
        @keyframes spin {
          to { transform: rotate(1turn); }
        }
      `}</style>
    </div>
  );
};

export default ProductDetail;